import React, { Component } from 'react'
import cx from 'classnames'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import { l } from '@manychat/manyui'

import FlowModel from 'common/flow/models/Flow'
import { analyticsService, flowBuilderEvents } from 'utils/services/analytics'

import app from '../app'
import { STATES, START_ID } from '../config'

import cm from './StatusBar.module.css'

const platform = get(window, 'navigator.platform', '')
const isMacPlatform =
  platform && typeof platform === 'string' && platform.toLowerCase().includes('mac')

const copyShortcutMacLabel = l.makeTranslate(
  `Use 'command (⌘) + c' to copy and 'command (⌘) + v' to paste steps`,
)
const copyShortcutWinLabel = l.makeTranslate(
  `Use 'control + c' to copy and 'control + v' to paste steps`,
)

const copyShortcutsLabel = isMacPlatform ? copyShortcutMacLabel : copyShortcutWinLabel

const MESSAGES = {
  deleted: {
    text: l.makeTranslate('This Automation is in the trash folder'),
    mode: 'danger',
  },
  published: {
    text: l.makeTranslate(`You are viewing a published version`),
    mode: 'warn',
  },
  greeting: l.makeTranslate(`👇 Tap some step to edit`),
  goToSidebar: l.makeTranslate(`👈 Edit step in sidebar`),
  useShiftForSelection: l.makeTranslate(`Hold 'shift' and click to select multiple steps`),
  newConnector: l.makeTranslate(`Select the next step or press 'esc' to cancel`),
  newConnectorByClick: l.makeTranslate(`Select the next step by clicking on it`),
  dragging: l.makeTranslate(`Hold 'shift' to move horizontally and vertically`),
  startHover: l.makeTranslate(
    `Drag and drop ‘Starting Step’ onto the step you want to start Automation with`,
  ),
  startDragging: l.makeTranslate(
    `Drop ‘Starting Step’ onto the step you want to start Automation with`,
  ),
  copyShortcuts: copyShortcutsLabel,
}

const ACTIONS = {
  edit: l.makeTranslate('Edit'),
  cancelNewConnector: l.makeTranslate('Cancel'),
}

// TODO: Maybe we should split StatusBar in separate components to enable their customization.
export default class StatusBar extends Component {
  static propTypes = {
    disallowEditMode: PropTypes.bool,
    isPostFlowViewer: PropTypes.bool,
  }

  state = {
    message: null,
    action: null,
    actionTitle: null,
  }

  // LifeCycle
  componentDidMount() {
    app.on('state', this.handleAppChange)
    app.on('hover', this.handleAppChange)
    this.reduceMessage(null, true)
  }

  componentWillUnmount() {
    app.removeListener('state', this.handleAppChange)
    app.removeListener('hover', this.handleAppChange)
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.disallowEditMode !== prevProps.disallowEditMode ||
      this.props.isPostFlowViewer !== prevProps.isPostFlowViewer
    ) {
      this.reduceMessage(this.props)
    }
  }

  // Methods
  setMode = (message, action = null, actionTitle = null) => {
    if (this.state.message !== message || this.state.action !== action) {
      this.setState({ message, action })
    }

    if (action != null) {
      this.setState({ actionTitle: actionTitle != null ? actionTitle : ACTIONS[action]() })
    }
  }

  reduceMessage = (props) => {
    props = props || this.props
    const { flow } = props

    if (FlowModel.isDeleted(flow)) {
      return this.setMode(MESSAGES.deleted)
    }

    if (FlowModel.isReadOnly(flow)) {
      return this.setMode(null)
    }

    if (props.readonly) {
      if (props.disallowEditMode) {
        return this.setMode(null)
      }

      if (props.isPostFlowViewer) {
        return this.setMode(MESSAGES.published, 'edit', l.translate('Copy & Edit'))
      }

      return this.setMode(MESSAGES.published, 'edit')
    }

    const hoveredNode = app.getHoveredNode()
    if (hoveredNode && hoveredNode.id === START_ID) {
      return this.setMode(MESSAGES.startHover)
    }

    switch (app.state) {
      case STATES.NORMAL:
        switch (Object.keys(props.selected).length) {
          case 0:
            return this.setMode(MESSAGES.greeting)
          case 1:
            if (hoveredNode && !props.selected[hoveredNode.id]) {
              return this.setMode(MESSAGES.useShiftForSelection)
            }
            return this.setMode(MESSAGES.goToSidebar)
          default:
            return this.setMode(MESSAGES.copyShortcuts)
        }
      case STATES.CREATING_NEW_CONNECTOR:
        if (app.stateOptions.byClick) {
          return this.setMode(MESSAGES.newConnectorByClick, 'cancelNewConnector')
        }
        return this.setMode(MESSAGES.newConnector)
      case STATES.DRAGGING:
        return this.setMode(MESSAGES.dragging)
      case STATES.DRAGGING_START:
        return this.setMode(MESSAGES.startDragging)
      default:
        return this.setMode(null)
    }
  }

  // EventHandlers
  handleActionClick = (e) => {
    switch (this.state.action) {
      case 'edit':
        analyticsService.logFlowBuilderEvent(flowBuilderEvents.GOTO_EDIT_MODE)
        this.props.onEdit()
        break
      case 'cancelNewConnector':
        app.resetState()
        break
    }
  }

  handleAppChange = (e) => this.reduceMessage()

  render() {
    let { sidebarHidden } = this.props
    let { message, action, actionTitle } = this.state

    if (message == null && action == null) {
      return null
    }

    if (typeof message !== 'object') {
      message = { text: message, mode: 'default' }
    }

    let { text, mode } = message

    return (
      <div className={cx(cm.wrapper, cm[mode], { [cm.withSidebar]: !sidebarHidden })}>
        {Boolean(l.getString(text)) && <span className={cm.text}>{l.getString(text)}</span>}
        {Boolean(action) && (
          <a onClick={this.handleActionClick} className={cm.action}>
            {actionTitle}
          </a>
        )}
      </div>
    )
  }
}
